// ═══════════════════════════════════════════════════════════════════════
// Round-fairness payload selection (pure) — feeds the history-chip modal.
//
// A round's provable-fairness story lives on two race events:
//   race_ready   → payload.serverSeedHash      (the COMMITMENT, pre-round)
//   race_settled → payload.fairness            (the REVEAL, post-settle)
// The crash multiplier + end time ride the race_ended event.
// Everything here is optional-tolerant: rounds without a reveal, pre-deploy
// rounds and unsettled rounds all degrade gracefully.
//
// FOUR GAME FAMILIES, one envelope. `fairness.game` is the discriminator:
// absent → `crash` (every round the crash engine ever published),
// `vehicle_boundaries` → the counting/CCTV games, `marble_order` → the marble
// races (a finishing ORDER, seed-derived), `birdie` → the golf putting game
// (B33: read off LIVE staging rows — the engine's shared fairness_runtime
// stamps `marble_order`, which Birdie's builder WRAPS, so the source is
// misleading and the live data is the authority). Any OTHER value defaults to
// crash: that is the back-compat rule for every round already published, and
// it is what a fifth family would fall into until it gets an arm here.
// DISPATCH ON THE REVEAL, never on the widget's game-type predicates: `crash`
// deliberately sits inside CCTV_GAME_TYPES (music suppression only), so
// isCCTVGameType would classify a crash round as a counting round.
// ═══════════════════════════════════════════════════════════════════════

import type { Race } from 'src/types/domain/Race'
import {
  readBirdieFairness,
  type BirdieRoundFairness
} from 'src/components/BirdieBoard/pastRounds'
import {
  isRaceEndedEvent,
  isRaceReadyEvent,
  isRaceSettledEvent,
  type RaceSettledEvent
} from 'src/types/domain/RaceEvent'
import {
  crashMultiplier as crashMultiplierV2,
  vehicleBoundaries,
  marbleOrder,
  OUTCOME_ORDER,
  WEIGHT_SCALE,
  type CountingOutcome,
  type CountingProbabilities
} from 'src/lib/fairness/mappers'
import { verifyCommitment, deriveR } from 'src/lib/fairness/verify'

/** The reveal's game family — the ONLY fairness discriminator. */
export type RoundFairnessGame = 'crash' | 'vehicle_boundaries' | 'marble_order' | 'birdie'

type WireFairness = NonNullable<RaceSettledEvent['payload']['fairness']>

/**
 * The beacon as the fairness core wants it: a lowercase-hex string ("" when
 * there is none).
 *
 * On the wire it is EITHER an object (crash: `{round, randomness}`) OR a bare
 * string (the counting engine publishes `""`). The old `fairness?.beacon?.
 * randomness ?? ''` read yielded '' for a string beacon only by luck — the
 * moment a non-empty string beacon ships it would silently derive a DIFFERENT
 * draw stream and every round would read "mismatch". Handle both explicitly.
 */
export function resolveBeaconRandomness(beacon: WireFairness['beacon']): string {
  if (typeof beacon === 'string') return beacon
  return beacon?.randomness ?? ''
}

/**
 * The counting (`vehicle_boundaries`) reveal, validated. Undefined unless the
 * payload carries EVERY field a verifier needs — a partial reveal must not
 * half-render a derivation, because a check that cannot run must never read
 * as a check that passed.
 */
export interface CountingReveal {
  outcome: CountingOutcome
  lowerBound: number
  upperBound: number
  gap: number
  /** Canonical order; index-aligned to `weightsPpm`. */
  outcomes: CountingOutcome[]
  /** Integer parts-per-million, summing to WEIGHT_SCALE. */
  weightsPpm: number[]
  /** label → 13-hex draw. Compared VERBATIM against the recomputation. */
  draws: Record<string, string>
  /** Camera-measured public INPUT — not seed-derived. */
  finalCount: number
}

const isCountingOutcome = (value: unknown): value is CountingOutcome =>
  typeof value === 'string' && (OUTCOME_ORDER as readonly string[]).includes(value)

const isPositiveIntArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((n) => typeof n === 'number' && Number.isInteger(n) && n >= 0)

/** Validate + normalise the counting half of a reveal. All-or-nothing. */
export function selectCountingReveal(fairness: WireFairness | undefined): CountingReveal | undefined {
  if (!fairness) return undefined
  const { outcome, lower_bound, upper_bound, gap, outcomes, weights_ppm, draws, final_count } =
    fairness
  if (!isCountingOutcome(outcome)) return undefined
  if (!Number.isInteger(lower_bound) || !Number.isInteger(upper_bound)) return undefined
  if (!Number.isInteger(gap) || !Number.isInteger(final_count)) return undefined
  if (!Array.isArray(outcomes) || outcomes.length === 0 || !outcomes.every(isCountingOutcome)) {
    return undefined
  }
  if (!isPositiveIntArray(weights_ppm) || weights_ppm.length !== outcomes.length) return undefined
  if (draws === null || typeof draws !== 'object' || Array.isArray(draws)) return undefined
  const drawEntries = Object.entries(draws)
  if (drawEntries.length === 0 || drawEntries.some(([, hex]) => typeof hex !== 'string')) {
    return undefined
  }

  return {
    outcome,
    lowerBound: lower_bound as number,
    upperBound: upper_bound as number,
    gap: gap as number,
    outcomes: [...outcomes],
    weightsPpm: [...weights_ppm],
    draws: { ...draws },
    finalCount: final_count as number
  }
}

/**
 * The marble (`marble_order`) reveal, validated. Undefined unless the payload
 * carries EVERY field a verifier needs — a partial reveal must not half-render a
 * derivation, because a check that cannot run must never read as one that passed.
 */
export interface MarbleReveal {
  /** The finishing order, 1st..Kth, by entrant name. */
  order: string[]
  /** The entrant universe for THIS round, in canonical order. */
  marbles: string[]
  /** Podium size (1 for single-winner games). */
  k: number
  /** Chosen k-permutation index (equal) or winner index (weighted). */
  index: number
  /** The single 13-hex "order" draw. Compared VERBATIM against recomputation. */
  draw: string
  /** Integer parts-per-million, index-aligned to `marbles` — WEIGHTED games
   *  only. Undefined for equal games. */
  weightsPpm?: number[]
}

const isNonEmptyStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((s) => typeof s === 'string')

/**
 * Validate + normalise the marble half of a reveal. All-or-nothing — never fail
 * open. A missing/empty entrant list, an out-of-range k, a non-hex-shaped draw,
 * an order whose length disagrees with k, or misaligned weights all yield
 * undefined (→ no derivation → the modal cannot go green). The mapper's own sum
 * guard is the backstop for a weights set that doesn't total 1_000_000.
 */
export function selectMarbleReveal(fairness: WireFairness | undefined): MarbleReveal | undefined {
  if (!fairness) return undefined
  const { order, marbles, k, index, draw, weights_ppm } = fairness
  if (!isNonEmptyStringArray(marbles)) return undefined
  if (!isNonEmptyStringArray(order)) return undefined
  if (!Number.isInteger(k) || (k as number) < 1 || (k as number) > marbles.length) return undefined
  if (!Number.isInteger(index) || (index as number) < 0) return undefined
  if (typeof draw !== 'string' || draw.length === 0) return undefined
  // The order is the K finishers; a length that disagrees with k is a malformed
  // reveal (and forces weighted rounds, whose order is the single winner, to k=1).
  if (order.length !== k) return undefined

  let weightsPpm: number[] | undefined
  if (weights_ppm !== undefined) {
    if (!isPositiveIntArray(weights_ppm) || weights_ppm.length !== marbles.length) return undefined
    weightsPpm = [...weights_ppm]
  }

  return {
    order: [...order],
    marbles: [...marbles],
    k: k as number,
    index: index as number,
    draw,
    weightsPpm
  }
}

/**
 * Published integer ppm weights → the probability map the mapper takes.
 * Exact round-trip: the mapper recomputes floor(p * 1e6 + 0.5), which recovers
 * the very integer we divided by, so this cannot re-price the distribution —
 * and the mapper's sum guard still fires on a set that does not total 1e6.
 */
function probabilitiesFromWeights(
  outcomes: readonly CountingOutcome[],
  weightsPpm: readonly number[]
): CountingProbabilities {
  const probabilities: CountingProbabilities = {}
  outcomes.forEach((outcome, index) => {
    probabilities[outcome] = (weightsPpm[index] ?? 0) / WEIGHT_SCALE
  })
  return probabilities
}

/** Draw maps are compared verbatim — same labels, same 13-hex values. */
function sameDraws(a: Record<string, string>, b: Record<string, string>): boolean {
  const keysA = Object.keys(a)
  if (keysA.length !== Object.keys(b).length) return false
  return keysA.every((key) => a[key] === b[key])
}

/** Finishing orders are compared position-by-position — same names, same rank. */
function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((name, i) => name === b[i])
}

export interface RoundFairnessInfo {
  roundId: string
  // First 8 hex chars of the round id — the modal's human handle.
  shortId: string
  // The reveal's game family — `crash` when the reveal omits `game` (every
  // round the crash engine published before the field existed).
  game: RoundFairnessGame
  // The crash multiplier (race_ended payload, falling back to the reveal's
  // own crash_multiplier). Undefined until the round ends — and always
  // undefined for a counting round, which has no multiplier.
  multiplier?: number
  // race_ended timestamp — "when" for the header.
  endedAt?: string
  // Pre-round commitment (DISPLAY only): race_ready when present, else the
  // reveal's own hash. The VERDICT uses observedCommitment (race_ready only).
  commitment?: string
  // The commitment as INDEPENDENTLY OBSERVED before the round (race_ready ONLY,
  // no settle fallback) — the only value that can certify "committed before you
  // bet". Undefined when race_ready was not seen for this round.
  observedCommitment?: string
  // v2 hash-chain reveal fields (undefined / 1 for legacy v1 rounds).
  schemeVersion?: number
  chainRootHash?: string
  chainIndex?: number
  beacon?: string
  // The revealed secret seed (+ nonce) — present only after settlement,
  // and only for rounds the engine published a reveal for.
  serverSeed?: string
  nonce?: string
  // Reveal-carried house edge (defaults to the engine's 0.06 when absent) —
  // feeds the in-modal derivation so it recomputes with the round's own
  // parameters, not a hardcoded assumption. CRASH ONLY: the counting family
  // keeps its margin in the payout odds table, never in the RNG, so there is
  // deliberately no house edge to carry (and none to invent).
  houseEdge?: number
  // The counting reveal, when this round is a `vehicle_boundaries` one AND
  // the payload is complete. Undefined for crash and for a partial reveal.
  counting?: CountingReveal
  // The marble reveal, when this round is a `marble_order` one AND the payload
  // is complete. Undefined for the other families and for a partial reveal.
  marble?: MarbleReveal
  // The Birdie reveal (B33), from the Birdie board's OWN guarded reader
  // (`pastRounds.readBirdieFairness` — field-by-field against the engine, with
  // its tests; there is deliberately no second selector here). Unlike the two
  // above, the KEY is only present on a birdie round: the three live families'
  // RoundFairnessInfo must stay byte-identical to what it was before this
  // family existed, and `roundFairnessGolden.test.ts` holds it to that.
  birdie?: BirdieRoundFairness
  // A race_settled event exists for the round.
  settled: boolean
}

export function selectRoundFairness(race: Race): RoundFairnessInfo {
  const readyEvent = race.raceEvents.find(isRaceReadyEvent)
  const endedEvent = race.raceEvents.find(isRaceEndedEvent)
  const settledEvent = race.raceEvents.find(isRaceSettledEvent)
  const fairness = settledEvent?.payload?.fairness
  // Absent `game` === crash: back-compat with every round already published.
  const game: RoundFairnessGame =
    fairness?.game === 'vehicle_boundaries'
      ? 'vehicle_boundaries'
      : fairness?.game === 'marble_order'
        ? 'marble_order'
        : fairness?.game === 'birdie'
          ? 'birdie'
          : 'crash'
  // The race_ready commitment is what a player could have recorded before
  // betting, so the Birdie reader prefers it over the reveal's own copy — the
  // same preference `commitment` below expresses for the chassis.
  const birdie =
    game === 'birdie'
      ? readBirdieFairness(settledEvent?.payload, readyEvent?.payload?.serverSeedHash)
      : undefined

  return {
    roundId: race.uuid,
    shortId: race.uuid.replace(/-/g, '').slice(0, 8),
    game,
    multiplier: endedEvent?.payload?.crash_multiplier ?? fairness?.crash_multiplier,
    endedAt: endedEvent?.eventTimestamp,
    commitment: readyEvent?.payload?.serverSeedHash ?? fairness?.server_seed_hash,
    // race_ready ONLY — no settle fallback (that would be tautological).
    observedCommitment: readyEvent?.payload?.serverSeedHash,
    // Default v2: every round is v2 (hash chain) going forward; a round with
    // no scheme_version is treated as v2, not the retired v1 seed+nonce.
    schemeVersion: fairness?.scheme_version ?? 2,
    chainRootHash: fairness?.chain_root_hash,
    chainIndex: fairness?.chain_index,
    beacon: resolveBeaconRandomness(fairness?.beacon),
    serverSeed: fairness?.server_seed,
    nonce: fairness?.nonce,
    houseEdge: fairness?.house_edge,
    counting: game === 'vehicle_boundaries' ? selectCountingReveal(fairness) : undefined,
    marble: game === 'marble_order' ? selectMarbleReveal(fairness) : undefined,
    // Key present on birdie rounds ONLY — see the field's note.
    ...(birdie === undefined ? {} : { birdie }),
    settled: settledEvent !== undefined
  }
}

/**
 * Does this ROUND have a provable-fairness story to open? — the honest gate
 * for every "open the fairness modal" affordance.
 *
 * Gate on the round's own artifacts, NOT on the game family: gating on
 * isCrashGameType left every counting round's modal dead, and each surface
 * gates the click target and the modal resolution independently, so relaxing
 * one without the other just moves the dead end.
 *
 * Deliberately NOT `selectRoundFairness(race).commitment !== undefined` even
 * though that is the same predicate: this runs per-row on every render of a
 * live results list, and the full selector zod-parses every event and
 * validates the counting payload for an answer that is two lookups wide.
 */
export function hasRoundFairness(race: Race): boolean {
  const readyEvent = race.raceEvents.find(isRaceReadyEvent)
  if (readyEvent?.payload?.serverSeedHash !== undefined) return true
  const fairness = race.raceEvents.find(isRaceSettledEvent)?.payload?.fairness
  return fairness?.server_seed_hash !== undefined || fairness?.server_seed !== undefined
}

// ═══════════════════════════════════════════════════════════════════════
// In-modal verification (v12.4 ask 8) — recompute the round from the
// revealed seed so the modal can SHOW the derivation (Aviator's
// hex/decimal/result table) instead of merely asserting it. Mirrors the
// engine byte-for-byte (155-game-crash/engine/multiplier.py):
//
//   commitment = sha256(utf-8 "{seed}:{nonce}")            (published pre-round)
//   hmac       = HMAC-SHA256(key = seed bytes, msg = utf-8 nonce)
//   r          = int(hmac[:13 hex chars]) / 2^52           (52-bit uniform)
//   result     = max(1.0, (1 - house_edge) / (1 - r)), rounded 2dp
//
// THE COMMITMENT COVERS THE NONCE — keep it that way. It used to be
// sha256(seed bytes) alone, which pinned the seed but not the outcome: the
// crash point is a function of BOTH seed and nonce, and the nonce is only
// revealed at settlement, so a nonce ground after the fact still satisfied
// sha256(revealed_seed) === commitment. This verifier would have rendered
// "verified" over a forged round. Hashing "{seed}:{nonce}" makes the published
// hash pin the outcome itself. The preimage is the utf-8 string, colon-joined,
// with the seed in its lowercase hex form exactly as revealed — a hex seed
// cannot contain ':', which is what makes the join unambiguous. This MUST stay
// byte-identical to engine/multiplier.py::commitment_for_seed or every round
// reports "not verified".
//
// The crash multiplier is UNCAPPED (Aviator/Bustabit) — no value ceiling; it
// is bounded naturally only by r's 52-bit precision. The house edge is baked
// straight into the payout ratio: RTP = (1 - edge) at every cashout target,
// and the ~edge instant-crash rate emerges naturally where (1 - edge)/(1 - r)
// < 1 (i.e. r < edge). No separate carve. The maximum WIN is capped by the
// operator's max payout in the Go backend, not by clamping the multiplier here.
//
// SECURITY: these run only on REVEALED data (settled rounds) — nothing
// here can precompute a live round because the seed is the input.
// ═══════════════════════════════════════════════════════════════════════

export const CRASH_HOUSE_EDGE = 0.06
// UNCAPPED (Aviator/Bustabit): no value ceiling on the crash multiplier. Kept
// as Infinity so the shared curve mirror below is genuinely unbounded (the max
// WIN is capped by operator max payout in the Go backend, not here).
export const CRASH_MAX_MULTIPLIER = Infinity
// 13 hex chars = 52 bits = exact IEEE-754 double mantissa precision.
const R_HEX_CHARS = 13
const R_DIVISOR = 2 ** 52

// The engine's crash curve (BYTE-IDENTICAL to 155-game-crash's
// crash_point_from_r): the edge lives in the numerator, so the payout is
// (1 - edge)/(1 - r), floored at 1.00x (r < edge → instant crash) and UNCAPPED
// above (no 100x ceiling — Aviator/Bustabit), rounded to 2dp (matching Python's
// round(m, 2)). `maxMultiplier` defaults to Infinity (no cap) and is retained
// only for signature symmetry with the engine.
export function crashPointFromR(
  r: number,
  houseEdge: number = CRASH_HOUSE_EDGE,
  maxMultiplier: number = CRASH_MAX_MULTIPLIER
): number {
  const raw = Math.max(1.0, Math.min((1 - houseEdge) / (1 - r), maxMultiplier))
  return Math.round(raw * 100) / 100
}

// Leading 52 bits of an HMAC hex digest → uniform r in [0, 1).
export function rFromHmacHex(hmacHex: string): { hex13: string; r: number } {
  const hex13 = hmacHex.slice(0, R_HEX_CHARS)
  return { hex13, r: parseInt(hex13, 16) / R_DIVISOR }
}

/**
 * The recomputed GAME RESULT, discriminated by family. The commitment and the
 * chain link are game-agnostic (they live on RoundDerivation); everything a
 * particular game derives from the seed lives here, so adding a family never
 * grows a pile of "crash-only, undefined elsewhere" fields.
 */
export type DerivedRoundResult =
  | {
      game: 'crash'
      // Leading 13 hex chars — the 52 bits that become r.
      hex13: string
      // The uniform decimal in [0, 1).
      r: number
      // The recomputed crash point.
      multiplier: number
    }
  | {
      game: 'vehicle_boundaries'
      outcome: CountingOutcome
      lowerBound: number
      upperBound: number
      gap: number
      outcomes: CountingOutcome[]
      weightsPpm: number[]
      // TWO entries: "outcome" plus the one branch label the outcome took.
      draws: Record<string, string>
    }
  | {
      game: 'marble_order'
      // The recomputed finishing order, 1st..Kth by entrant name.
      order: string[]
      marbles: string[]
      k: number
      index: number
      // The single "order" draw the whole order came from.
      draw: string
      // WEIGHTED games only; undefined for equal games.
      weightsPpm?: number[]
    }

export interface RoundDerivation {
  schemeVersion: number
  // Recomputed commitment (v1: sha256("seed:nonce"); v2: sha256(seed)).
  recomputedCommitment: string
  // true = the INDEPENDENTLY-OBSERVED pre-round commitment (race_ready) matches;
  // false = mismatch; undefined = no pre-round commitment was observed, so
  // "committed before you bet" is UNPROVEN and MUST NOT render "verified".
  commitmentMatches?: boolean
  // v2 only: sha256^chainIndex(seed) === chainRootHash. undefined for v1.
  chainLinksToRoot?: boolean
  // The recomputed result === the published one. crash: the multiplier (v2
  // exact; v1 ±0.005 seam). counting: outcome + both bounds + every draw,
  // compared verbatim. undefined = nothing published to compare against.
  resultMatches?: boolean
  // The per-game recomputation itself.
  result: DerivedRoundResult
}

/**
 * Recompute a settled round from its revealed seed via the shared, game-agnostic
 * verifier (src/lib/fairness) — v1 (seed+nonce) and v2 (hash chain) alike
 * (WebCrypto, async). Returns null when it can't run (malformed seed, missing
 * counting payload, no SubtleCrypto). The commitment check uses the
 * INDEPENDENTLY-OBSERVED pre-round value only, so a round the player did not
 * observe committed cannot read "verified" off a tautological recompute.
 *
 * `game` selects the result mapper: 'crash' (default) derives the multiplier
 * curve; 'vehicle_boundaries' replays the weighted outcome + boundary draws
 * around the reveal's measured `finalCount`; 'marble_order' replays the single
 * "order" draw into the finishing order and compares it to the published one.
 * 'birdie' has NO client-side recompute (B33): the public verifier's Birdie
 * mappers land in M6 and nothing here may invent one, so it returns null —
 * no derivation, therefore nothing a modal could word as "verified" — rather
 * than falling into the crash arm and deriving a multiplier from a putting
 * reveal.
 */
export async function deriveRoundVerification(input: {
  serverSeed: string
  game?: RoundFairnessGame
  schemeVersion?: number
  nonce?: string
  beacon?: string
  chainRootHash?: string
  chainIndex?: number
  observedCommitment?: string
  multiplier?: number
  houseEdge?: number
  counting?: CountingReveal
  marble?: MarbleReveal
}): Promise<RoundDerivation | null> {
  if (!globalThis.crypto?.subtle) return null
  // Birdie: no recompute exists yet, so no derivation — never the crash arm.
  if (input.game === 'birdie') return null
  // Malformed seed (non-hex / odd-length / empty) -> null, never a throw.
  if (!/^[0-9a-f]+$/i.test(input.serverSeed) || input.serverSeed.length % 2 !== 0) return null

  // Default to v2 (every crash round is v2 going forward). A legacy v1 round is
  // recognised only by the presence of a per-round nonce — v2 has none — so the
  // shared core still verifies one correctly if ever encountered, without the
  // modal ever defaulting a real (nonce-less) round to the retired v1 path.
  const schemeVersion = input.schemeVersion ?? (input.nonce != null ? 1 : 2)
  const v2 = schemeVersion >= 2
  const proof = {
    schemeVersion,
    serverSeed: input.serverSeed,
    nonce: input.nonce,
    beacon: input.beacon ?? '',
    chainRootHash: input.chainRootHash,
    chainIndex: input.chainIndex,
    observedCommitment: input.observedCommitment
  }

  try {
    const commit = await verifyCommitment(proof)
    if (commit === null) return null
    const shared = {
      schemeVersion,
      recomputedCommitment: commit.recomputedCommitment,
      commitmentMatches: commit.commitmentVerified,
      chainLinksToRoot: commit.chainLinksToRoot
    }

    if (input.game === 'vehicle_boundaries') {
      // A counting round is unverifiable without its reveal: `finalCount` is a
      // camera MEASUREMENT, and the published weights are what the outcome was
      // drawn against. No payload → null (no derivation, and therefore no
      // verdict), never a half-check that could read as a pass.
      const counting = input.counting
      if (!counting) return null
      const recomputed = await vehicleBoundaries(
        input.serverSeed,
        probabilitiesFromWeights(counting.outcomes, counting.weightsPpm),
        counting.finalCount,
        { beacon: proof.beacon, gap: counting.gap }
      )
      const resultMatches =
        recomputed.outcome === counting.outcome &&
        recomputed.lowerBound === counting.lowerBound &&
        recomputed.upperBound === counting.upperBound &&
        sameDraws(recomputed.draws, counting.draws)

      return { ...shared, resultMatches, result: { game: 'vehicle_boundaries', ...recomputed } }
    }

    if (input.game === 'marble_order') {
      // A marble round is unverifiable without its reveal: the entrant universe
      // and k are what the "order" draw is scaled against. No payload → null
      // (no derivation, therefore no verdict), never a half-check that reads as
      // a pass. The mapper throws on a malformed weight set (bad sum / bad
      // length); the surrounding try turns that into null, i.e. non-green.
      const marble = input.marble
      if (!marble) return null
      const recomputed = await marbleOrder(input.serverSeed, marble.marbles, marble.k, {
        beacon: proof.beacon,
        weightsPpm: marble.weightsPpm ?? null
      })
      const resultMatches =
        sameOrder(recomputed.order, marble.order) &&
        recomputed.index === marble.index &&
        recomputed.draw === marble.draw

      return { ...shared, resultMatches, result: { game: 'marble_order', ...recomputed } }
    }

    const r = await deriveR(proof, 'crash', 0)
    if (r === null) return null

    const houseEdge = input.houseEdge ?? CRASH_HOUSE_EDGE
    // v2 pins floor(x*100+0.5)/100 (exact cross-language); v1 keeps the legacy
    // Math.round curve so historical rounds verify against their stored 2dp.
    const multiplier = v2 ? crashMultiplierV2(r, houseEdge) : crashPointFromR(r, houseEdge)
    const hex13 = Math.floor(r * R_DIVISOR)
      .toString(16)
      .padStart(R_HEX_CHARS, '0')

    const resultMatches =
      input.multiplier !== undefined
        ? v2
          ? multiplier === input.multiplier
          : Math.abs(multiplier - input.multiplier) < 0.005
        : undefined

    return { ...shared, resultMatches, result: { game: 'crash', hex13, r, multiplier } }
  } catch {
    return null
  }
}

// ── Right-side past-results entries (ShowRaceResults' crash variant) ────
// The race list → the heat-tinted pill entries next to the bet-history
// affordance (v8: relocated from the old top-center strip). Settled rounds
// only, store order preserved (newest first), rounds without a positive
// crash multiplier dropped, capped at `max`.
export interface CrashPastResult {
  raceId: string
  multiplier: number
  endedAt: string
}

export function deriveCrashPastResults(races: readonly Race[], max: number): CrashPastResult[] {
  const out: CrashPastResult[] = []
  for (const race of races) {
    if (out.length >= max) break
    // Inline settled check (no raceUtils import — keeps this module free of
    // the `effect` dependency so it stays trivially unit-testable).
    if (!race.raceEvents.some((e) => e.eventType === 'race_settled')) continue
    const endedEvent = race.raceEvents.find(isRaceEndedEvent)
    const multiplier = endedEvent?.payload?.crash_multiplier ?? 0
    if (endedEvent === undefined || multiplier <= 0) continue
    out.push({ raceId: race.uuid, multiplier, endedAt: endedEvent.eventTimestamp })
  }
  return out
}

// The modal's open/close resolution: a chip click selects a round id; the
// modal renders only while that id still resolves to a known race (rounds
// get pruned from the store as history rolls).
export function resolveModalRace(races: readonly Race[], selectedId: string | null): Race | null {
  if (selectedId === null) return null
  return races.find((race) => race.uuid === selectedId) ?? null
}

// Shared with the history pills: colour tier per multiplier.
export function getMultiplierTier(multiplier: number): string {
  if (multiplier >= 10) return 'crash-multiplier-pill--huge'
  if (multiplier >= 5) return 'crash-multiplier-pill--high'
  if (multiplier >= 2) return 'crash-multiplier-pill--good'
  if (multiplier >= 1.5) return 'crash-multiplier-pill--mid'
  return 'crash-multiplier-pill--low'
}

// Shared with the history pills: relative "when" strings. v47: routed
// through i18n — these rendered hardcoded English inside the otherwise
// fully-translated fairness modal (the game's trust surface) and the pill
// tooltips. Both call sites already hold a `t`.
export function formatTimeAgo(
  timestamp: string,
  t: (key: string, opts?: object) => string
): string {
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000)
  if (seconds < 10) return t('Just now')
  if (seconds < 60) return t('{{count}}s ago', { count: seconds })
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('{{count}}m ago', { count: minutes })
  const hours = Math.floor(minutes / 60)
  return t('{{count}}h ago', { count: hours })
}

// `first8…last4` for long hex values shown inline (full value still goes to
// the clipboard on copy).
export function shortenHash(hash: string): string {
  if (hash.length <= 12) return hash
  return `${hash.slice(0, 8)}…${hash.slice(-4)}`
}

// ── Counting-round presentation helpers ────────────────────────────────

/**
 * The ONE branch draw a counting round took, e.g. `under_offset`. The reveal
 * publishes exactly two draws — "outcome" and the branch the outcome selected
 * — so this is the other one. Undefined only for a malformed reveal.
 */
export function countingBranchDraw(
  draws: Record<string, string>
): { label: string; hex13: string } | undefined {
  const entry = Object.entries(draws).find(([label]) => label !== 'outcome')
  return entry ? { label: entry[0], hex13: entry[1] } : undefined
}

/** Integer ppm → a display percentage (trailing zeros trimmed). */
export function formatWeightPercent(ppm: number): string {
  const percent = (ppm / WEIGHT_SCALE) * 100
  return `${parseFloat(percent.toFixed(4))}%`
}
